<script setup lang="ts">
  import type { Table } from '@tanstack/vue-table';
  import { computed } from 'vue';

  import { Button } from '@ui/components/button';
  import LoadingSpinner from '@ui/components/LoadingSpinner.vue';
  import TableSelection from '@ui/components/table/TableSelection.vue';
  import { cn } from '@ui/utils/utils';

  import { useTaskCollectionStore } from '@/store';

  interface DataTablePaginationProps {
    globalSelection?: any[];
    loading?: boolean;
    noSelect?: boolean;
    table: Table<any>;
    totalRecords?: number;
    v1?: boolean;
  }

  const props = defineProps<DataTablePaginationProps>();

  const emits = defineEmits<{
    (e: 'clearSelection'): void;
  }>();

  const taskCollectionStore = useTaskCollectionStore();

  const hasNextPage = computed<boolean>(() => {
    const { pageIndex, pageSize } = props.table.getState().pagination;
    const numRecords = pageSize * (pageIndex + 1);
    if (props.totalRecords !== undefined) {
      return props.totalRecords > numRecords;
    }

    return props.table.getRowCount() !== pageSize;
  });

  const getItemRange = () => {
    if (props.loading) return '';

    const numRows = props.table.getRowModel().rows.length;
    if (numRows === 0) return 'No results';

    const { pageIndex, pageSize } = props.table.getState().pagination;
    const start = pageIndex * pageSize;
    const end = start + Math.min(pageSize, numRows);

    return end === 0 ? '0' : `${start + 1} - ${end}`;
  };
</script>

<template>
  <div class="w-full flex items-center rounded justify-between gap-2 flex-wrap bg-background">
    <TableSelection
      v-if="!noSelect"
      :table
      :global-selection
      @clear-selection="emits('clearSelection')"
    >
      <template v-if="$slots.actions" #actions>
        <slot name="actions" />
      </template>
    </TableSelection>

    <div :class="cn('flex gap-2.5 mr-2.5 items-center', noSelect ? 'ml-auto' : 'lg:ml-auto')">
      <LoadingSpinner v-if="loading" />

      <div v-else>
        <span v-if="getItemRange()"> {{ getItemRange() }} </span>
        <template v-if="totalRecords && !loading">
          of {{ totalRecords.toLocaleString() }}
        </template>
      </div>

      <div class="space-x-2.5">
        <Button
          variant="outline"
          size="icon-sm"
          :disabled="!table.getCanPreviousPage() || taskCollectionStore.isLoadingCollectionTasks"
          @click="table.previousPage()"
        >
          <i class="far fa-arrow-left" />
        </Button>

        <Button
          variant="outline"
          size="icon-sm"
          :disabled="!hasNextPage || taskCollectionStore.isLoadingCollectionTasks"
          @click="table.nextPage()"
        >
          <i class="far fa-arrow-right" />
        </Button>
      </div>
    </div>
  </div>
</template>
